import { __spreadArray } from 'tslib';
import clsx from 'clsx';
import React, { lazy, useState, useRef, useEffect, Suspense, useMemo } from 'react';
import { Checkbox } from '../checkboxes/index.js';
import { cssDropdownWrapper, cssDropdownButton, cssDropdownOptionsContainer, cssDropdownOption, cssDropdownOptionFocused } from './index.css.js';

var Clear = lazy(function () { return import('./Clear.js'); });
var clearTypeAhead;
function useOptionsFromChildrenAndProps(children, options) {
    return useMemo(function () {
        if (options) {
            return options;
        }
        else if (!options && children && Array.isArray(children)) {
            return children.map(function (o) { return ({
                label: o.props.children,
                value: o.props.value,
                disabled: !!o.props.disabled
            }); });
        }
        else if (!options && children && !Array.isArray(children)) {
            return [
                {
                    label: children.props.children,
                    value: children.props.value,
                    disabled: !!children.props.disabled
                }
            ];
        }
        else {
            return [];
        }
    }, [children, options]);
}
var getSelectedValue = function (selectedOptions, Options, placeholder) {
    var isArray = Array.isArray(selectedOptions);
    var isObj = typeof selectedOptions === 'object';
    var isNumberArray = isArray && typeof selectedOptions[0] === 'number';
    if (placeholder && !selectedOptions) {
        return [-1];
    }
    else if (!selectedOptions) {
        return [0];
    }
    else if (!isArray && !isObj) {
        return [selectedOptions];
    }
    else if (isNumberArray) {
        return selectedOptions;
    }
    else if (isArray && !isNumberArray) {
        return Options.map(function (e) { return selectedOptions.findIndex(function (s) { return s.value === e.value; }) || -1; }).filter(function (e) { return e === -1; });
    }
    else if (!isArray && isObj) {
        return [Options.findIndex(function (e) { return e.value === selectedOptions.value; })];
    }
    return [];
};
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useSelectedOptionIndexes(selectedOptions, placeholder, Options, multiple) {
    var _a = useState(getSelectedValue(selectedOptions, Options, placeholder)), selected = _a[0], setSelected = _a[1];
    useEffect(function () {
        setSelected(getSelectedValue(selectedOptions, Options, placeholder));
    }, [selectedOptions, Options, placeholder]);
    return {
        selected: selected,
        addSelected: function (i) {
            return multiple ? setSelected(__spreadArray(__spreadArray([], selected.filter(function (s) { return s !== -1; }), true), [i], false)) : setSelected([i]);
        },
        removeSelected: function (i) {
            return multiple ? setSelected(selected.filter(function (s) { return s !== i; })) : setSelected([placeholder ? -1 : 0]);
        },
        clearSelected: function () { return setSelected([placeholder ? -1 : 0]); },
        getPreview: function () {
            var _a;
            if (!selected || selected.length === 0 || selected[0] === -1) {
                return React.createElement("span", null, placeholder);
            }
            if (multiple) {
                return (React.createElement(React.Fragment, null, selected.map(function (option, idx) {
                    var _a;
                    var label = (_a = Options[option]) === null || _a === void 0 ? void 0 : _a.label;
                    var separator = idx < selected.length - 1 ? React.createElement("span", null, ", ") : React.createElement("span", null);
                    return (React.createElement("span", { key: idx },
                        typeof label === 'string' ? React.createElement("span", null, label) : label,
                        separator));
                })));
            }
            var label = (_a = Options[selected[0]]) === null || _a === void 0 ? void 0 : _a.label;
            return typeof label === 'string' ? React.createElement("span", null, label) : label;
        }
    };
}
var numDropdowns = 0;
var Dropdown = function (_a) {
    var _b;
    var _c = _a.placeholder, placeholder = _c === void 0 ? 'Select' : _c, multiple = _a.multiple, children = _a.children, options = _a.options, popoverStyle = _a.popoverStyle, optionStyle = _a.optionStyle, placeholderStyle = _a.placeholderStyle, label = _a.label, _d = _a.hideLabel, hideLabel = _d === void 0 ? false : _d, selectedOptions = _a.selectedOptions, onSelect = _a.onSelect, onPreview = _a.onPreview, clearAll = _a.clearAll, _e = _a.required, required = _e === void 0 ? false : _e, className = _a.className, disabled = _a.disabled, _f = _a.hideSelectTag, hideSelectTag = _f === void 0 ? false : _f, style = _a.style;
    var _g = useState(false), menuOpen = _g[0], setMenuOpen = _g[1];
    var id = useState(function () { return numDropdowns++; })[0];
    var Options = useOptionsFromChildrenAndProps(children, options);
    var _h = useSelectedOptionIndexes(selectedOptions, placeholder, Options, multiple), selected = _h.selected, addSelected = _h.addSelected, removeSelected = _h.removeSelected, getPreview = _h.getPreview, clearSelected = _h.clearSelected;
    var _j = useState(0), focused = _j[0], setFocused = _j[1];
    var containerRef = useRef(null);
    var wrapperRef = useRef(null);
    var offContainerClick = function (e) {
        if (!!e && !!e.target && !!wrapperRef && !!wrapperRef.current) {
            var targetNode = e.target;
            if (!!targetNode && !wrapperRef.current.contains(targetNode)) {
                setMenuOpen(false);
            }
        }
    };
    useEffect(function () {
        window.addEventListener('click', offContainerClick, false);
        return function () {
            window.removeEventListener('click', offContainerClick, false);
        };
    }, []);
    var onSelectOption = function (o, i) {
        return function (e) {
            e.preventDefault();
            if (o.disabled) {
                return;
            }
            setMenuOpen(false);
            var isCurrentIncluded = selected.includes(i);
            if (isCurrentIncluded) {
                if (multiple) {
                    removeSelected(i);
                }
            }
            else {
                addSelected(i);
            }
            if (onSelect) {
                var allSelected_1 = [];
                selected.forEach(function (idx) {
                    if (idx > -1) {
                        if (!(isCurrentIncluded && i === idx)) {
                            allSelected_1.push(Options[idx]);
                        }
                    }
                });
                if (!isCurrentIncluded) {
                    allSelected_1.push(Options[i]);
                }
                onSelect(o, i, allSelected_1);
            }
        };
    };
    useEffect(function () {
        if (containerRef.current) {
            if (menuOpen) {
                containerRef.current.focus();
            }
            else if (selected.length === 0 || selected.includes(-1)) {
                containerRef.current.blur();
            }
        }
    }, [menuOpen, selected]);
    var _k = useState(''), typeAhead = _k[0], setTypeAhead = _k[1];
    var KeyEventHandler = function (e) {
        switch (e.key) {
            case 'Enter':
                if (!menuOpen) {
                    setMenuOpen(true);
                }
                else {
                    setMenuOpen(false);
                    if (focused !== -1)
                        selected.includes(focused) ? removeSelected(focused) : addSelected(focused);
                }
                break;
            case 'Escape':
                if (menuOpen) {
                    setMenuOpen(false);
                }
                break;
            case 'ArrowDown':
                if (!menuOpen)
                    setMenuOpen(true);
                setFocused(function (prev) {
                    if (prev === -1) {
                        return 0;
                    }
                    else if (prev + 1 >= Options.length) {
                        return 0;
                    }
                    else {
                        return prev + 1;
                    }
                });
                break;
            case 'ArrowUp':
                setFocused(function (prev) {
                    if (prev === -1) {
                        return Options.length - 1;
                    }
                    else if (prev - 1 < 0) {
                        return Options.length - 1;
                    }
                    else {
                        return prev - 1;
                    }
                });
                break;
            default:
                if (!multiple) {
                    var found_1 = false;
                    var newTypeAhead = typeAhead + e.key;
                    setTypeAhead(newTypeAhead);
                    var pattern_1 = new RegExp("^".concat(newTypeAhead), 'ig');
                    Options.forEach(function (option, idx) {
                        if (!found_1 && pattern_1.test(String(option.label))) {
                            setFocused(idx);
                            addSelected(idx);
                            setMenuOpen(false);
                            onSelect && onSelect(option, idx, []);
                            clearTimeout(clearTypeAhead);
                            found_1 = true;
                        }
                    });
                    if (found_1) {
                        clearTypeAhead = setTimeout(function () {
                            setTypeAhead('');
                        }, 3000);
                    }
                    else {
                        setTypeAhead('');
                        setMenuOpen(false);
                    }
                }
                break;
        }
    };
    var handleClear = function () {
        setMenuOpen(false);
        clearSelected();
        if (onSelect) {
            onSelect({}, -1, []);
        }
    };
    var handleOnClick = function () {
        if (disabled) {
            setMenuOpen(false);
            return;
        }
        if (!menuOpen && multiple)
            setFocused(-1);
        if (!menuOpen && !multiple)
            setFocused(selected[0]);
        setMenuOpen(!menuOpen);
    };
    var selectedOptionNodes = typeof onPreview === 'function' ? onPreview(selected) : getPreview();
    var labelId = "dropdown-label-".concat(id);
    return (React.createElement("div", { ref: wrapperRef, className: clsx('c-dropdown', 'c-dropdown--wrap', cssDropdownWrapper, className, {
            'c-dropdown--multiple': multiple
        }), style: style },
        !!label && !hideLabel && (React.createElement("label", { id: labelId, className: clsx('c-input-label', {
                required: required
            }) }, label)),
        hideSelectTag ? null : (React.createElement("select", { name: 'option', value: (_b = Options[selected[0]]) === null || _b === void 0 ? void 0 : _b.value }, Options.map(function (o, i) { return (React.createElement("option", { key: i, value: o === null || o === void 0 ? void 0 : o.value }, String(o === null || o === void 0 ? void 0 : o.label))); }))),
        React.createElement("button", { className: clsx('c-dropdown__container', cssDropdownButton, {
                'c-dropdown__container--has-value': selected.length !== 0 && !selected.includes(-1)
            }), disabled: disabled, "aria-expanded": menuOpen ? 'true' : 'false', "aria-label": hideLabel && typeof label === 'string' ? label : undefined, "aria-labelledby": hideLabel ? undefined : labelId, onClick: handleOnClick, style: selected[0] === -1 ? placeholderStyle : optionStyle, tabIndex: 0, onKeyDown: KeyEventHandler }, selectedOptionNodes),
        React.createElement("div", { style: popoverStyle, className: clsx('c-dropdown__options-container', cssDropdownOptionsContainer, {
                'c-dropdown--active': menuOpen
            }) },
            clearAll && (React.createElement(Suspense, { fallback: '' },
                React.createElement(Clear, { onClick: handleClear }))),
            Options.map(function (o, i) {
                var _a;
                return (React.createElement("div", { style: optionStyle, onClick: onSelectOption(o, i), key: i, "aria-selected": selected.includes(i), tabIndex: i + 1, className: clsx('c-dropdown__option', cssDropdownOption, (_a = {
                            'c-dropdown__option--disabled': o.disabled
                        },
                        _a[cssDropdownOptionFocused] = focused === i,
                        _a)) },
                    multiple && (React.createElement(Checkbox, { onClick: onSelectOption(o, i), checked: selected.includes(i), disabled: o.disabled })),
                    React.createElement("span", null, o === null || o === void 0 ? void 0 : o.label)));
            }))));
};

export { Dropdown };
//# sourceMappingURL=index.js.map
